import { useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';

// Genera un sonido sutil y nítido de notificación usando Web Audio API sin dependencias externas
const playChimeSound = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    const now = ctx.currentTime;
    osc.frequency.setValueAtTime(587.33, now); // Nota D5
    osc.frequency.setValueAtTime(880, now + 0.12); // Nota A5

    gain.gain.setValueAtTime(0.12, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.38);
  } catch (e) {
    // Ignorar si el navegador bloquea audio antes de interacción
  }
};

// Sonido muy leve y discreto para avisos ligeros (tickets pendientes)
const playSubtlePopSound = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    const now = ctx.currentTime;
    osc.frequency.setValueAtTime(659.25, now); // Nota E5 suave

    gain.gain.setValueAtTime(0.04, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.15);
  } catch (e) {
    // Ignorar si el navegador bloquea audio
  }
};

export const useSupportGlobalHub = ({ token, currentUserId, selectedTicketId, onNavigateToTicket }) => {
  const [incomingToast, setIncomingToast] = useState(null);
  const [pendingToast, setPendingToast] = useState(null);
  const notifiedPendingTicketsRef = useRef(new Set());
  const selectedTicketRef = useRef(selectedTicketId);
  selectedTicketRef.current = selectedTicketId;

  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;

  const navigateRef = useRef(onNavigateToTicket);
  navigateRef.current = onNavigateToTicket;

  const handleIncomingActivity = useCallback(async (data) => {
    if (!data) return;

    const idTicket = Number(data.idTicket || data.ticketId || data.message?.idTicket);
    if (!idTicket) return;

    const msg = data.message || data;
    const isClient = msg.bEsAgente === 0 || msg.bEsAgente === false || msg.isAgent === false;
    const assignedClientName = (data.vCustomerName || msg.vCustomerName || '').trim();

    const currentOpenId = Number(selectedTicketRef.current);
    const isCurrentlyOpen = Boolean(currentOpenId && currentOpenId === idTicket);

    const idUsuarioAsignado = data.idUsuarioAsignado ?? msg.idUsuarioAsignado;
    const iEstadoTicket = data.iEstadoTicket !== undefined && data.iEstadoTicket !== null
      ? Number(data.iEstadoTicket)
      : (msg.iEstadoTicket !== undefined && msg.iEstadoTicket !== null ? Number(msg.iEstadoTicket) : null);

    const idUsuarioEmisor = data.idUsuarioEmisor ? Number(data.idUsuarioEmisor) : null;
    const isBot = idUsuarioEmisor === 14 ||
      String(msg.vCreatedUser || msg.createdUser || data.vCreatedUser || data.createdUser || '').toUpperCase() === 'AKASIABOT';
    const usuariosNotificar = Array.isArray(data.usuariosNotificar)
      ? data.usuariosNotificar.map(Number)
      : [];

    const myUserId = Number(currentUserIdRef.current);

    // Disparar evento DOM para que las vistas activas (TicketsView, etc.) se sincronicen reactivamente de forma silenciosa
    window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
      detail: {
        idTicket,
        vTextoMensaje: msg.vTextoMensaje || msg.textoMensaje || msg.messageText || '',
        dUltimaActividad: msg.dCreatedDate || msg.createdDate || new Date().toISOString(),
        dFechaRespuestaInicial: msg.dFechaRespuestaInicial,
        bEsAgente: msg.bEsAgente ?? (msg.isAgent ? 1 : 0),
        iEstadoTicket,
        usuariosNotificar,
        isCurrentlyOpen,
        vCustomerName: assignedClientName,
        idClienteAkasia: data.idClienteAkasia ?? msg.idClienteAkasia
      }
    }));

    // Si el mensaje es de un cliente en un ticket cerrado o reabierto, emitir evento para recargar historial
    if (isClient) {
      window.dispatchEvent(new CustomEvent('akasia:support:ticket_history_refresh', {
        detail: {
          idTicket,
          iEstadoTicket
        }
      }));
    }

    // Los mensajes enviados por asesores son salientes; nunca deben generar alertas sonoras, toasts ni notificaciones
    if (!isClient && !isBot) {
      return;
    }

    // Si el mensaje fue enviado por el propio usuario actual, no auto-notificarse (no aplica para el bot)
    if (!isBot && idUsuarioEmisor && myUserId && idUsuarioEmisor === myUserId) {
      return;
    }

    // Si el usuario ya tiene abierto este ticket en pantalla, el chat ya se actualiza en vivo con ReceiveMessage: no alertar toast
    if (isCurrentlyOpen) {
      return;
    }

    // No alertar en tickets formalmente cerrados (2)
    if (iEstadoTicket === 2) {
      return;
    }

    // Regla estricta: Si el ticket tiene usuarios a notificar (responsable o colaboradores), solo les llega a ellos (salvo que sea el bot)
    if (!isBot && usuariosNotificar.length > 0) {
      if (!myUserId || !usuariosNotificar.includes(myUserId)) {
        return;
      }
    }

    // Si el ticket está en progreso (3) y está asignado a otro usuario, no notificar salvo que el usuario actual sea colaborador o el bot
    if (!isBot && iEstadoTicket === 3 && idUsuarioAsignado && Number(idUsuarioAsignado) !== myUserId && !usuariosNotificar.includes(myUserId)) {
      return;
    }

    const isMultiNumero = Boolean(data.esOtroNumero || data.isOtherNumber);
    const isEnProgreso = iEstadoTicket === 3 || Boolean(idUsuarioAsignado);
    const isResponsable = Number(idUsuarioAsignado) === myUserId;
    const isColaborador = !isResponsable && (usuariosNotificar.includes(myUserId) || (!isClient && !isBot));
    const isPendiente = !isEnProgreso && (iEstadoTicket === 1 || iEstadoTicket === null);
    const isNewTicket = Boolean(data.bIsNew || data.isNew || data.isNewTicket);

    if (isEnProgreso) {
      notifiedPendingTicketsRef.current.delete(idTicket);
    }

    // Obtener detalles del mensaje y emisor
    const whatsappSenderName = (msg.vCreatedUser || msg.createdUser || '').trim();

    const senderTitle = isBot
      ? 'Akasia Bot'
      : isClient
        ? (assignedClientName || whatsappSenderName || `Cliente Ticket #${idTicket}`)
        : (whatsappSenderName || `Asesor Ticket #${idTicket}`);

    const messageText = msg.vTextoMensaje || msg.textoMensaje || msg.messageText || 'Nuevo mensaje recibido';

    const isWindowBackground = typeof document !== 'undefined' && (document.hidden || !document.hasFocus());

    // Regla para tickets que NO están en progreso (aplica solo para clientes, los mensajes de Akasia Bot se siguen mostrando igual en su toast interactivo):
    // 1. Si llega por primera vez (isNewTicket = true y no registrado previamente), se deja como estaba:
    //    pasa a la notificación interactiva completa (abajo a la derecha con sonido normal playChimeSound).
    // 2. Si el ticket NO está en progreso y son mensajes subsecuentes del cliente, se muestra la notificación ligera (abajo a la izquierda con playSubtlePopSound).
    if (!isBot && !isEnProgreso && (!isNewTicket || notifiedPendingTicketsRef.current.has(idTicket))) {
      // Sonido leve y discreto
      playSubtlePopSound();

      // Notificación nativa ligera si la ventana está en segundo plano
      if (isWindowBackground && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        try {
          const notif = new Notification(`[Ticket Pendiente #${idTicket}] · ${senderTitle}`, {
            body: messageText,
            icon: '/favicon.ico',
            tag: `ticket-pending-${idTicket}`
          });

          notif.onclick = () => {
            window.focus();
            if (navigateRef.current) {
              navigateRef.current(idTicket);
            }
            notif.close();
          };
        } catch (err) {
          console.warn('[SupportGlobalHub] Error mostrando notificación nativa de ticket pendiente:', err);
        }
      }

      // Notificación ligera abajo a la izquierda para mensajes que no están en progreso
      setPendingToast({
        idTicket,
        customerName: senderTitle,
        messageText,
        categoriaTexto: 'Pendiente',
        timestamp: Date.now()
      });

      return;
    }

    // Registrar apertura inicial del ticket para que subsecuentes mensajes no en progreso usen la notificación ligera
    if (isNewTicket) {
      notifiedPendingTicketsRef.current.add(idTicket);
    }

    let tipoNotificacion = isBot ? 'bot' : (isEnProgreso ? (isColaborador ? 'colaborador' : 'responsable') : 'abierto');
    let categoriaTexto = isBot ? 'Akasia Bot' : (isMultiNumero ? 'Cliente (Otro número)' : (isNewTicket ? 'Nuevo Ticket' : 'Ticket Abierto'));

    if (!isBot && !isMultiNumero && isEnProgreso) {
      if (isColaborador) {
        if (isClient) {
          categoriaTexto = 'Colaborador · Cliente';
        } else {
          const senderUser = msg.vCreatedUser || msg.createdUser || 'Asesor';
          categoriaTexto = `Colaborador · ${senderUser}`;
        }
      } else if (isClient) {
        categoriaTexto = 'En Progreso';
      } else {
        const senderUser = msg.vCreatedUser || msg.createdUser || 'Colaborador';
        categoriaTexto = `Colaborador · ${senderUser}`;
      }
    }

    // 1. Sonido de alerta
    playChimeSound();

    if (isWindowBackground && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        const notif = new Notification(`[${categoriaTexto}] Ticket #${idTicket} · ${senderTitle}`, {
          body: messageText,
          icon: '/favicon.ico',
          tag: `ticket-${idTicket}`,
          renotify: true
        });

        notif.onclick = () => {
          window.focus();
          if (navigateRef.current) {
            navigateRef.current(idTicket);
          }
          notif.close();
        };
      } catch (err) {
        console.warn('[SupportGlobalHub] Error mostrando notificación nativa:', err);
      }
    }

    // 3. Mostrar Toast flotante interactivo en pantalla
    setIncomingToast({
      idTicket,
      customerName: senderTitle,
      messageText,
      tipo: tipoNotificacion,
      categoriaTexto,
      timestamp: Date.now()
    });
  }, []);

  // Conexión persistente de SignalR a nivel módulo Support Engine
  useEffect(() => {
    if (!token) return;

    let isCancelled = false;
    let connection = null;

    const startHub = async () => {
      try {
        const resp = await fetch('/api/SupportEngine/GetBaseUrl', {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
        });
        if (!resp.ok) {
          console.error(`❌ [SupportGlobalHub] Error al consultar /api/SupportEngine/GetBaseUrl: HTTP ${resp.status}`);
          return;
        }
        if (isCancelled) return;
        const { baseUrl } = await resp.json();
        if (isCancelled || !baseUrl) return;

        const hubUrl = `${baseUrl}/hubs/support-chat`;
        connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => token,
            skipNegotiation: false,
            transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
          })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        connection.onreconnecting(err => {
          console.warn('🔄 [SupportGlobalHub] SignalR reconectando...', err);
        });

        connection.onclose(err => {
          console.warn('🔌 [SupportGlobalHub] SignalR conexión cerrada:', err);
        });

        connection.on('ReceiveTicketActivity', (data) => {
          handleIncomingActivity(data);
        });

        await connection.start();
      } catch (err) {
        console.error('❌ [SupportGlobalHub] Error en la conexión SignalR centralizada:', err);
      }
    };

    startHub();

    return () => {
      isCancelled = true;
      if (connection) {
        connection.stop().catch(() => { });
      }
    };
  }, [token, handleIncomingActivity]);

  const dismissToast = useCallback(() => {
    setIncomingToast(null);
  }, []);

  const dismissPendingToast = useCallback(() => {
    setPendingToast(null);
  }, []);

  return {
    incomingToast,
    dismissToast,
    pendingToast,
    dismissPendingToast
  };
};

export default useSupportGlobalHub;
